<script setup lang="ts">
import type { SiteImageResource } from "@carnet/api/backoffice";

defineProps<{
  items: SiteImageResource[];
  label?: string;
}>();

const { t } = useI18n();

const failedUrls = reactive(new Set<string>());
</script>

<template>
  <AppCarousel
    v-if="items.length"
    :items="items"
    :item-key="(item, index) => `${item.image.url}-${index}`"
    :label="label ?? t('carousel.images.label')"
    class="[--carousel-gap:--spacing(2.5)] [--carousel-slide-size:auto]"
    overflow-visible="lg"
    :ratio="4 / 3"
  >
    <template #default="{ item, index }">
      <figure class="flex justify-center overflow-hidden bg-elevated" :class="failedUrls.has(item.image.url) ? 'aspect-4/3 w-full' : 'h-full w-fit'">
        <AppImage
          :src="item.image.url"
          :alt="item.alt || t('carousel.images.fallbackAlt', { current: index + 1, total: items.length })"
          img-class="h-full w-auto max-w-none shrink-0"
          @load="failedUrls.delete(item.image.url)"
          @error="failedUrls.add(item.image.url)"
        />
        <figcaption v-if="item.legend" class="sr-only">{{ item.legend }}</figcaption>
      </figure>
    </template>
    <template #baseline="{ selectedIndex, scrollSnaps }">
      <div class="flex gap-x-2 text-sm text-gray-600" aria-live="polite" aria-atomic="true">
        <span v-if="scrollSnaps.length > 1" class="tabular-nums shrink-0 whitespace-nowrap">{{ selectedIndex + 1 }}/{{ scrollSnaps.length }}</span>
        <p v-if="items[selectedIndex]?.legend">
          {{ items[selectedIndex].legend }}
        </p>
      </div>
    </template>
  </AppCarousel>
</template>
